import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import 'react-datetime/css/react-datetime.css';

import MaskedDateTimeField from 'components/forms/MaskedDateTimeField';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';

import type { EditableParameter } from '../../edit/EditableParameter';

const toOptionsArray = (s: string): string[] =>
  s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

const toKeyValueArray = (s: string): { key: string; label: string }[] =>
  s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((pair) => {
      const [key, ...rest] = pair.split('=');
      return { key: (key ?? '').trim(), label: rest.join('=').trim() };
    })
    .filter(({ key, label }) => key && label);

const normalizeKV = (s: string): string => {
  const i = s.indexOf('=');
  return i >= 0 ? s.slice(0, i).trim() : s.trim();
};

// A datetimezone default_expression is "YYYY-MM-DDTHH:mm:ss <Zone>". Split into the datetime and zone parts.
const parseDateTimeZone = (raw: string | null): { datetime: string; timezone: string } => {
  const str = String(raw ?? '').trim();
  if (!str) return { datetime: '', timezone: moment.tz.guess() };
  const spaceIndex = str.lastIndexOf(' ');
  if (spaceIndex > 0) {
    return { datetime: str.slice(0, spaceIndex).trim(), timezone: str.slice(spaceIndex + 1).trim() || moment.tz.guess() };
  }
  return { datetime: str, timezone: moment.tz.guess() };
};

/** The Default Expression control(s) for one parameter — the shape depends on its type. Reads/writes the model field. */
export const ParameterDefaultExpression = observer(({ parameter }: { readonly parameter: EditableParameter }) => {
  // Local, per-card UI state: the timezone chosen before a datetime exists (nothing is written to the model until there's a
  // datetime part, matching the original behaviour).
  const [pendingTz, setPendingTz] = useState<string>(() => parseDateTimeZone(parameter.defaultExpression).timezone);

  const type = parameter.parameterType.replace(/\s+/g, '').toLowerCase();
  const expr = parameter.defaultExpression;
  const exprStr = String(expr ?? '').trim();
  const setExpr = (value: string | null) => (parameter.defaultExpression = value || null);

  if (type === 'dropdown' || type === 'keyvaluedropdown') {
    const isKv = type === 'keyvaluedropdown';
    const options = parameter.options ?? '';
    const flat = isKv ? toKeyValueArray(options) : toOptionsArray(options).map((v) => ({ key: v, label: v }));
    const selectOptions: EditSelectOptionList<string> = [
      { key: '', value: '', label: '' },
      ...flat.map((o) => ({ key: o.key, value: o.key, label: isKv ? `${o.label} (${o.key})` : o.label })),
    ];
    const current = isKv ? normalizeKV(exprStr) : exprStr;
    const value = flat.some((o) => o.key === current) ? current : '';
    return (
      <div className="d-flex gap-3">
        <div className="flex-fill">
          <EditString
            label={isKv ? 'Options (e.g. 01=Test, 02=Test2)' : 'Options (comma-separated)'}
            value={parameter.options}
            onChange={(v) => (parameter.options = v)}
          />
        </div>
        <div className="flex-fill">
          <EditSelect label="Default Expression" value={value} onChange={setExpr} options={selectOptions} disabled={flat.length === 0} />
        </div>
      </div>
    );
  }

  const isDate = type === 'date';
  const isDateTime = type === 'datetime';
  const isDateTimeZone = type === 'datetimezone';
  const isTime = type === 'time';

  if (isDate || isDateTime || isDateTimeZone || isTime) {
    const parsedTz = isDateTimeZone ? parseDateTimeZone(expr) : null;
    const dateStr = parsedTz ? parsedTz.datetime : exprStr;
    const format = isDate ? 'YYYY-MM-DD' : isTime ? 'HH:mm:ss' : moment.ISO_8601;
    const parsedMoment = dateStr ? moment(dateStr, format as string, true) : null;
    const pickerValue = parsedMoment && parsedMoment.isValid() ? parsedMoment : '';
    // A timezone can be picked before a datetime exists: prefer the pending selection, since parseDateTimeZone never yields an
    // empty timezone (it falls back to the guessed zone), which would otherwise discard a tz-first pick.
    const tz = parsedTz ? pendingTz || parsedTz.timezone : '';

    const onPick = (dt: moment.Moment) => {
      if (isDate) return setExpr(dt.clone().seconds(0).milliseconds(0).format('YYYY-MM-DD'));
      if (isTime) return setExpr(dt.format('HH:mm:ss'));
      const stamp = dt.clone().milliseconds(0).format('YYYY-MM-DDTHH:mm:ss');
      return setExpr(isDateTimeZone ? `${stamp} ${tz}` : stamp);
    };

    return (
      <div className="d-flex flex-wrap align-items-center gap-2">
        <div className="flex-fill" style={{ minWidth: '12rem' }}>
          <MaskedDateTimeField
            label="Default Expression"
            value={pickerValue}
            onChange={onPick}
            mode={isTime ? 'time' : isDate ? 'date' : 'datetime'}
            showCalendar={!isTime}
          />
        </div>
        {isDateTimeZone && (
          <div className="flex-fill" style={{ minWidth: '12rem' }}>
            <EditTimeZoneReference
              label="Time Zone"
              value={tz}
              onChange={(next) => {
                const zone = next ?? moment.tz.guess();
                setPendingTz(zone);
                const { datetime } = parseDateTimeZone(expr);
                if (datetime) setExpr(`${datetime} ${zone}`);
              }}
            />
          </div>
        )}
        {(isDate || isDateTime || isDateTimeZone) && (
          <div className="flex-fill" style={{ minWidth: '10rem' }}>
            <EditString label="Output Format (e.g. DD-MM-YYYY)" value={parameter.outputFormat} onChange={(v) => (parameter.outputFormat = v)} />
          </div>
        )}
        <Button variant="outline-secondary" onClick={() => setExpr('')} disabled={exprStr.length === 0} title="Clear">
          Clear
        </Button>
      </div>
    );
  }

  return <EditString label="Default Expression" value={parameter.defaultExpression} onChange={setExpr} />;
});
